/* global axios */
// DahReply — all calls proxied through Rails backend.
// KNOWLEDGE_API_KEY injected server-side; no secret in the browser bundle.
//
// Ads are rows with a stable `ad_id` (BASE/ads...). Shapes (checked on staging 2026-10-09):
//   ad:   { ad_id, inbox_id, match_type: 'prefilled_msg'|'ads_id', campaign, ads_name, prefilled_message,
//           ads_id, platform_code, ads_setting, auto_response_enabled, auto_response_message,
//           created_at, updated_at, deleted_at, stats?: { conversations, appointments, conversion_rate } }
//   GET /ads -> { data: ad[], total, page, limit };  POST/PATCH/restore -> { ad }
//   GET /ads/{id}/performance -> { ad, totals, series?: [{ period, conversations, appointments, conversion_rate }] }
// Immutable after creation: ad_id, match_type, prefilled_message, ads_id (422 immutable_field).
// The inbox default type (`ads_tracking_mode`) is only exposed by the legacy GET ads-config.

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const inboxBase = (accountId, inboxId) =>
  `/chatbot/accounts/${accountId}/inboxes/${inboxId}`;

const state = {
  adsConfig: [],
  ads: [],
  adsTrackingMode: 'prefilled_msg',
  isLoading: false,
  isError: false,
};

const getters = {};

const mutations = {
  SET_ADS_CONFIG(state, config) {
    state.adsConfig = config;
  },
  SET_ADS(state, ads) {
    state.ads = ads;
  },
  SET_ADS_TRACKING_MODE(state, mode) {
    state.adsTrackingMode = mode;
  },
  SET_IS_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  SET_IS_ERROR(state, isError) {
    state.isError = isError;
  },
};

const actions = {
  async fetchAdsConfig({ commit, rootGetters }, inboxId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_LOADING', true);
      commit('SET_IS_ERROR', false);

      const res = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/ads-config`)
      );

      commit('SET_ADS_CONFIG', res.data.inbox_metadata || []);
      commit('SET_ADS_TRACKING_MODE', res.data.ads_tracking_mode || 'prefilled_msg');
      return res.data;
    } catch (error) {
      console.error('Fetch ads config failed:', error);
      commit('SET_IS_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  // All ads of the inbox, active and deleted (split client-side by `deleted_at`),
  // plus the inbox default type from the legacy route.
  async fetchAds({ commit, rootGetters }, inboxId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_LOADING', true);
      commit('SET_IS_ERROR', false);

      const [legacy, ads] = await Promise.all([
        axios.get(knowledgeProxy(accountId, `${inboxBase(accountId, inboxId)}/ads-config`)),
        (async () => {
          const all = [];
          for (let page = 1; page <= 20; page += 1) {
            // eslint-disable-next-line no-await-in-loop
            const res = await axios.get(knowledgeProxy(accountId, `${inboxBase(accountId, inboxId)}/ads`), {
              params: { status: 'all', include_stats: true, limit: 100, page },
            });
            all.push(...(res.data.data || []));
            if (all.length >= (res.data.total || 0)) break;
          }
          return all;
        })(),
      ]);

      commit('SET_ADS', ads);
      commit('SET_ADS_CONFIG', legacy.data.inbox_metadata || []);
      commit('SET_ADS_TRACKING_MODE', legacy.data.ads_tracking_mode || 'prefilled_msg');
      return ads;
    } catch (error) {
      console.error('Fetch ads failed:', error);
      commit('SET_IS_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  // matchType blank = inbox default. Errors carry { error, message, ... }
  // (409 duplicate_key | deleted_ad_exists, 422 validation_error).
  async createAd({ rootGetters }, { inboxId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `${inboxBase(accountId, inboxId)}/ads`),
        payload
      );
      return res.data.ad || res.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Only the editable fields: campaign, ads_name, platform_code, ads_setting,
  // auto_response_enabled, auto_response_message. Works on deleted ads too.
  async updateAd({ rootGetters }, { inboxId, adId, patch }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `${inboxBase(accountId, inboxId)}/ads/${adId}`),
        patch
      );
      return res.data.ad || res.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Soft delete by default (kept in the Deleted view); permanent=true only works
  // on an already deleted ad and also removes its tracking history.
  async deleteAd({ rootGetters }, { inboxId, adId, permanent = false }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `${inboxBase(accountId, inboxId)}/ads/${adId}`),
        { params: permanent ? { permanent: true } : {} }
      );
      return res.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async restoreAd({ rootGetters }, { inboxId, adId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `${inboxBase(accountId, inboxId)}/ads/${adId}/restore`)
      );
      return res.data.ad || res.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // params: { start_date, end_date, group_by: 'day'|'week'|'month' (omit = totals only) }
  async fetchAdPerformance({ rootGetters }, { inboxId, adId, params = {} }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `${inboxBase(accountId, inboxId)}/ads/${adId}/performance`),
        { params }
      );
      return res.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  async updateAdsConfig({ rootGetters }, { inboxId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/ads-config`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Update ads config failed:', error);
      throw error.response?.data || error;
    }
  },

  async addAdsConfigEntry({ rootGetters }, { inboxId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/ads-config`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Add ads config entry failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteAdsConfigEntry({ rootGetters }, { inboxId, index }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/ads-config/${index}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete ads config entry failed:', error);
      throw error.response?.data || error;
    }
  },

  // Blob of the server-generated template (xlsx|csv); its required columns
  // follow the inbox's current tracking mode.
  async downloadAdsImportTemplate({ rootGetters }, { inboxId, format = 'xlsx' }) {
    const accountId = rootGetters.getCurrentAccountId;
    const res = await axios.get(
      knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/ads-config/import/template`),
      { params: { format }, responseType: 'blob' }
    );
    return res.data;
  },

  // dryRun=true validates only (nothing saved). Success returns the report;
  // a 422 row-validation failure throws the same report shape (has `errors`),
  // other failures throw `{ error }` / `{ detail }` / the axios error.
  async importAdsConfig({ rootGetters }, { inboxId, file, mode = 'merge', dryRun = false, onDeleted = 'error' }) {
    const accountId = rootGetters.getCurrentAccountId;
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/inboxes/${inboxId}/ads-config/import`),
        form,
        { params: { dry_run: dryRun, mode, on_deleted: onDeleted } }
      );
      return res.data;
    } catch (error) {
      console.error('Import ads config failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchAdsAnalytics({ rootGetters }, params = {}) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/ads-analytics`),
        { params }
      );
      return res.data;
    } catch (error) {
      console.error('Fetch ads analytics failed:', error);
      throw error.response?.data || error;
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
